import { useLayoutEffect, type RefObject } from 'react';

/**
 * Keeps the split header table's columns aligned with the body table when
 * `tableLayout="auto"`.
 *
 * Under `table-layout: fixed` the two tables render identical `<colgroup>`s
 * derived from the same `visibleColumns()` array, so they cannot drift. Under
 * `auto` each table would size its columns from its *own* content - the
 * header from its labels, the body from its cells - and the two would
 * disagree. This hook measures the first body row whose cell count matches
 * the column count (skipping `colSpan` rows such as the empty state or the
 * load-more affordance) and copies those pixel widths onto the header table's
 * `<col>` elements. The header table itself is always `table-fixed`, so the
 * copied widths are applied exactly rather than renegotiated against the
 * header labels' own min-content.
 *
 * Runs as a layout effect so the first paint is already aligned, and re-syncs
 * through a `ResizeObserver` on the body table, which fires whenever the
 * table's box changes - container resizes, rows appearing or disappearing.
 *
 * The dependency list is what decides when the observer is torn down and the
 * measurement re-run wholesale, and each entry earns its place:
 * - `enabled` - toggling `tableLayout` must attach or detach the machinery;
 * - `columnCount` - adding, removing or `visible`-toggling a column re-renders
 *   both tables without necessarily changing the body table's outer box, so
 *   the ResizeObserver may never fire; without this dep the header would keep
 *   widths measured for the previous column set;
 * - `rows` - new data can redistribute auto-layout column widths while
 *   leaving the table's box identical (same row count, different content),
 *   which is again invisible to the ResizeObserver;
 * - the two refs - stable objects from `useRef`, listed for exhaustiveness.
 * Everything cheaper than that (pure size changes) is the observer's job, and
 * `sync` re-reads the DOM on every pass, so nothing it touches needs to be a
 * dependency.
 */
export function useAutoColumnWidthSync(
    enabled: boolean,
    headerTableRef: RefObject<HTMLTableElement>,
    bodyTableRef: RefObject<HTMLTableElement>,
    columnCount: number,
    rows: readonly unknown[]
): void {
    useLayoutEffect(() => {
        if (!enabled) {
            return undefined;
        }
        const headerTable = headerTableRef.current;
        const bodyTable = bodyTableRef.current;
        if (!headerTable || !bodyTable) {
            return undefined;
        }

        const sync = () => {
            // Re-queried on every pass, not captured once: React recreates the
            // `<col>` elements when column keys change, and a captured list
            // would go on styling detached nodes.
            const cols = Array.from(headerTable.querySelectorAll('col'));
            const bodyRows = Array.from(bodyTable.tBodies[0]?.rows ?? []);
            const source = bodyRows.find((row) => row.cells.length === cols.length);
            if (!source) {
                // No measurable row (a colSpan-only body: empty state, error
                // state) - fall back to the fixed header table's own even
                // distribution rather than keeping stale pixel widths around.
                cols.forEach((col) => col.style.removeProperty('width'));
                return;
            }
            Array.from(source.cells).forEach((cell, index) => {
                cols[index]?.style.setProperty('width', `${cell.getBoundingClientRect().width}px`);
            });
        };

        sync();
        const observer = new ResizeObserver(sync);
        observer.observe(bodyTable);
        return () => observer.disconnect();
    }, [enabled, columnCount, rows, headerTableRef, bodyTableRef]);
}
